import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface BreadcrumbItem {
  name: string;
  path: string;
}

/**
 * Breadcrumb map – keys must be quoted because they contain characters
 * that are not valid identifier names (e.g. "/", "/about/history").
 */
const breadcrumbMap: Record<string, BreadcrumbItem[]> = {
  '/': [{ name: '홈', path: '/' }],
  '/about': [
    { name: '홈', path: '/' },
    { name: '학원소개', path: '/about' },
  ],
  '/about/history': [
    { name: '홈', path: '/' },
    { name: '학원소개', path: '/about' },
    { name: '연혁', path: '/about/history' },
  ],
  '/about/level-class': [
    { name: '홈', path: '/' },
    { name: '학원소개', path: '/about' },
    { name: '레벨별 수업', path: '/about/level-class' },
  ],
  '/about/small-class': [
    { name: '홈', path: '/' },
    { name: '학원소개', path: '/about' },
    { name: '소규모 수업', path: '/about/small-class' },
  ],
  '/about/management': [
    { name: '홈', path: '/' },
    { name: '학원소개', path: '/about' },
    { name: '확실한 관리', path: '/about/management' },
  ],
  '/about/scholarship': [
    { name: '홈', path: '/' },
    { name: '학원소개', path: '/about' },
    { name: '장학 혜택', path: '/about/scholarship' },
  ],
  '/curriculum': [
    { name: '홈', path: '/' },
    { name: '커리큘럼', path: '/curriculum' },
  ],
  '/curriculum/math': [
    { name: '홈', path: '/' },
    { name: '커리큘럼', path: '/curriculum' },
    { name: '수학', path: '/curriculum/math' },
  ],
  '/curriculum/english': [
    { name: '홈', path: '/' },
    { name: '커리큘럼', path: '/curriculum' },
    { name: '영어', path: '/curriculum/english' },
  ],
  '/curriculum/korean': [
    { name: '홈', path: '/' },
    { name: '커리큘럼', path: '/curriculum' },
    { name: '국어', path: '/curriculum/korean' },
  ],
  '/curriculum/science': [
    { name: '홈', path: '/' },
    { name: '커리큘럼', path: '/curriculum' },
    { name: '과학', path: '/curriculum/science' },
  ],
  '/curriculum/winter-school': [
    { name: '홈', path: '/' },
    { name: '커리큘럼', path: '/curriculum' },
    { name: '겨울방학특강', path: '/curriculum/winter-school' },
  ],
  '/teachers': [
    { name: '홈', path: '/' },
    { name: '강사진', path: '/teachers' },
  ],
  '/results': [
    { name: '홈', path: '/' },
    { name: '합격자 현황', path: '/results' },
  ],
  '/results/admission': [
    { name: '홈', path: '/' },
    { name: '합격자 현황', path: '/results' },
    { name: '대입 합격 실적', path: '/results/admission' },
  ],
  '/results/school': [
    { name: '홈', path: '/' },
    { name: '합격자 현황', path: '/results' },
    { name: '내신 성적 실적', path: '/results/school' },
  ],
  '/notice': [
    { name: '홈', path: '/' },
    { name: '공지사항', path: '/notice' },
  ],
  '/campus': [
    { name: '홈', path: '/' },
    { name: '캠퍼스 안내', path: '/campus' },
  ],
  '/campus/jihang': [
    { name: '홈', path: '/' },
    { name: '캠퍼스 안내', path: '/campus' },
    { name: '지행캠퍼스', path: '/campus/jihang' },
  ],
  '/campus/okjeong': [
    { name: '홈', path: '/' },
    { name: '캠퍼스 안내', path: '/campus' },
    { name: '옥정캠퍼스', path: '/campus/okjeong' },
  ],
  '/campus/millak': [
    { name: '홈', path: '/' },
    { name: '캠퍼스 안내', path: '/campus' },
    { name: '민락캠퍼스', path: '/campus/millak' },
  ],
  '/contact': [
    { name: '홈', path: '/' },
    { name: '상담 문의', path: '/contact' },
  ],
  '/news/faq': [
    { name: '홈', path: '/' },
    { name: '자주 묻는 질문', path: '/news/faq' },
  ],
  '/news/reviews': [
    { name: '홈', path: '/' },
    { name: '학부모 후기', path: '/news/reviews' },
  ],
};

const BASE_URL = 'https://eliteoffuture1.com';

/**
 * BreadcrumbSchema injects a JSON‑LD script into the <head> that describes the
 * current page's breadcrumb trail for SEO.
 *
 * The component is deliberately side‑effect only and returns null.
 */
export default function BreadcrumbSchema(): null {
  const location = useLocation();

  useEffect(() => {
    const schemaId = 'schema-breadcrumb';
    
    try {
      const pathname = location.pathname;

      // -----------------------------------------------------------------------
      // Determine breadcrumb items
      // -----------------------------------------------------------------------
      let items: BreadcrumbItem[];

      // Special handling for notice detail pages (excluding admin)
      if (pathname.startsWith('/notice/') && pathname !== '/notice/admin') {
        items = [
          { name: '홈', path: '/' },
          { name: '공지사항', path: '/notice' },
          { name: '공지사항 상세', path: pathname },
        ];
      } else {
        items = breadcrumbMap[pathname] ?? [{ name: '홈', path: '/' }];
      }

      // -----------------------------------------------------------------------
      // Build the JSON‑LD structure
      // -----------------------------------------------------------------------
      const schema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: `${BASE_URL}${item.path}`,
        })),
      };

      // -----------------------------------------------------------------------
      // Insert (or replace) the <script> tag safely
      // -----------------------------------------------------------------------
      const existing = document.getElementById(schemaId);
      if (existing) {
        existing.remove();
      }

      const script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    } catch (err) {
      // Fail‑soft: log the error silently without breaking the UI
      console.error('[BreadcrumbSchema] Error:', err);
      
      // Remove any existing schema on error
      const existing = document.getElementById(schemaId);
      if (existing) {
        existing.remove();
      }
    }

    // Cleanup on unmount or pathname change
    return () => {
      try {
        const el = document.getElementById(schemaId);
        if (el) {
          el.remove();
        }
      } catch (err) {
        // Silent cleanup error
        console.error('[BreadcrumbSchema] Cleanup error:', err);
      }
    };
  }, [location.pathname]);

  // This component does not render anything visible
  return null;
}
